import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { cn } from "@/lib/utils";
import { CompanyDTO, ConversationDTO } from "@shaarp/common";
import { Loader2 } from "lucide-react";
import {
  ChatConversation,
  ChatConversationView,
  MemoryMessageToSend,
} from "./chat-conversation";
import { ChatNew } from "./chat-new";
import { buildOptimisticUserMessage } from "./optimistic-message";

export function ChatLayout({
  conversation,
  pendingMessage,
  triggerCreateNewConversation = async () => {},
  onMessageGenerated = async () => {},
  onPendingMessageSent,
  companyTargeted = null,
  isCreatingConversation = false,
  isNewConversation,
  onStopCreatingConversation = () => {},
  draftToRestore,
  onDraftRestored,
}: {
  conversation: ConversationDTO | undefined | null;
  pendingMessage?: MemoryMessageToSend | null;
  triggerCreateNewConversation?: (message: MemoryMessageToSend) => void;
  onMessageGenerated?: (conversation: ConversationDTO) => void;
  onPendingMessageSent?: () => void;
  /**
   * The company the chat is about, null for the standalone chat. Every
   * composer before the conversation exists gets it — the welcome screen's,
   * then the one shown while the conversation is created — as the
   * conversation's own composer will: what they promise depends on it.
   */
  companyTargeted?: CompanyDTO | null;
  isCreatingConversation?: boolean;
  /** See `ChatConversation`: the page knows, the pending message does not. */
  isNewConversation?: boolean;
  /** Stop, pressed while the conversation is being created. */
  onStopCreatingConversation?: () => void;
  /** Text the welcome screen's composer takes back. */
  draftToRestore?: { text: string; attempt: number } | null;
  /** The welcome screen's composer took the draft back. */
  onDraftRestored?: () => void;
}) {
  const { isMobile } = useDeviceFormat();

  return (
    <div
      className={cn(
        "absolute flex flex-col w-full h-full bg-muted/50",
        isMobile && "pb-(--mobile-bottom-inset)"
      )}
    >
      {conversation?.id ? (
        <ChatConversation
          key={conversation.id}
          conversation={conversation}
          pendingMessage={pendingMessage}
          isNewConversation={isNewConversation}
          onMessageGenerated={onMessageGenerated}
          onPendingMessageSent={onPendingMessageSent}
        />
      ) : isCreatingConversation && pendingMessage ? (
        <ChatConversationView
          messages={[buildOptimisticUserMessage(pendingMessage, null)]}
          status="submitted"
          onMessageSent={() => {}}
          streamInProgress
          triggerStopStream={onStopCreatingConversation}
          initialCompanyContext={companyTargeted}
          // Takes over from the welcome screen, where the user pressed send.
          composerStartsFromSend
        />
      ) : isCreatingConversation ? (
        <div className="flex items-center justify-center w-full h-full">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ChatNew
          onMessageSent={triggerCreateNewConversation}
          companyTargeted={companyTargeted}
          draftToRestore={draftToRestore}
          onDraftRestored={onDraftRestored}
        />
      )}
    </div>
  );
}
